import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface DraftAlternative {
  text: string;
  approach: string;
  confidence: number;
}

interface AlternativesPickerProps {
  alternatives: DraftAlternative[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export const AlternativesPicker = ({
  alternatives,
  selectedIndex,
  onSelect,
}: AlternativesPickerProps): JSX.Element => {
  if (alternatives.length <= 1) {
    return <></>;
  }

  return (
    <div className="rounded-lg border bg-card p-3 text-sm">
      <div className="mb-2 text-xs font-semibold text-muted-foreground">
        Alternatives (Press 1-3 to switch)
      </div>
      <div className="space-y-2">
        {alternatives.map((alternative, index) => (
          <button
            key={`${alternative.approach}-${index}`}
            type="button"
            onClick={() => onSelect(index)}
            aria-pressed={index === selectedIndex}
            className={cn(
              'flex w-full flex-col gap-1 rounded-md border px-2 py-1.5 text-left text-xs transition',
              index === selectedIndex
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border text-muted-foreground hover:bg-muted'
            )}
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span className="font-medium">
                [{index + 1}] {alternative.approach}
              </span>
              <Badge variant="outline" className="shrink-0 text-[10px]">
                {Math.round(alternative.confidence * 100)}%
              </Badge>
            </span>
            {/*
              The alternative's own words. Until 2026-08-12 this component rendered ONLY the
              `approach` label and the confidence badge, so the operator was asked to choose
              between three AI drafts while being shown none of what any of them said — the
              `text` field was carried all the way here from brain.ts and never read.

              The selected alternative is already visible in full in the editor below, so it
              stays clamped here; the ones you are choosing BETWEEN are the ones that need to
              be legible, and they expand to five lines. `whitespace-pre-line` keeps the
              paragraph breaks the model wrote — a LinkedIn DM is read as paragraphs, and
              collapsing them makes two genuinely different drafts look identical at a glance.
            */}
            <span
              className={cn(
                'w-full whitespace-pre-line text-muted-foreground',
                index === selectedIndex ? 'line-clamp-2' : 'line-clamp-5'
              )}
              data-testid={`alternative-body-${index}`}
            >
              {alternative.text}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
